import React from 'react';

import { ButtonBase, CircularProgress, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';

export type WorkflowBannerSeverity = 'info' | 'success' | 'warning';

const useStyles = makeStyles<{ severity: WorkflowBannerSeverity }>()((theme, { severity }) => {
  const accent = getSeverityColor(theme, severity);
  return {
    action: {
      '&:disabled': { color: theme.appColors.iconsTypeMuted },
      '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}` },
      '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
      borderRadius: 6,
      color: theme.palette.primary.main,
      flexShrink: 0,
      fontSize: 14,
      fontWeight: 600,
      gap: theme.spacing(0.75),
      padding: theme.spacing(0.5, 1),
      transition: 'background-color 0.15s ease',
      whiteSpace: 'nowrap',
    },
    actionSecondary: {
      '&:hover': { backgroundColor: theme.appColors.hoverBackgroundSubtle },
      color: theme.palette.text.secondary,
    },
    actions: {
      alignItems: 'center',
      display: 'flex',
      flexShrink: 0,
      gap: theme.spacing(0.5),
    },
    iconBadge: {
      alignItems: 'center',
      backgroundColor: alpha(accent, 0.14),
      borderRadius: '50%',
      color: accent,
      display: 'inline-flex',
      flexShrink: 0,
      height: 28,
      justifyContent: 'center',
      width: 28,
    },
    root: {
      alignItems: 'center',
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.appColors.ruleSubtle}`,
      borderLeft: `3px solid ${accent}`,
      borderRadius: 8,
      boxSizing: 'border-box',
      display: 'flex',
      gap: theme.spacing(1.5),
      padding: theme.spacing(1, 1, 1, 1.5),
      width: '100%',
    },
    subtitle: {
      color: theme.palette.text.secondary,
      fontSize: 14,
      minWidth: 0,
    },
    /** Heading and subtitle share a line when they fit and stack when they don't. */
    text: {
      alignItems: 'center',
      columnGap: theme.spacing(1),
      display: 'flex',
      flex: 1,
      flexWrap: 'wrap',
      minWidth: 0,
      rowGap: 2,
    },
    title: {
      color: theme.palette.text.primary,
      fontSize: 14,
      fontWeight: 600,
    },
  };
});

interface WorkflowBannerCardProps {
  heading: string;
  icon: React.ComponentType<{ size?: number }>;
  severity: WorkflowBannerSeverity;
  /** Sits at the right edge, usually one or two {@link WorkflowBannerAction}s. */
  action?: React.ReactNode;
  dataCy?: string;
  subtitle?: React.ReactNode;
}

/** The workflow page's status card: a severity-colored edge and icon, a heading, and its actions. */
export const WorkflowBannerCard = React.memo(function WorkflowBannerCard({
  action,
  dataCy,
  heading,
  icon: Icon,
  severity,
  subtitle,
}: WorkflowBannerCardProps): JSX.Element {
  const { classes } = useStyles({ severity });
  return (
    <div aria-label={heading} className={classes.root} data-cy={dataCy} role="alert">
      <span aria-hidden className={classes.iconBadge}>
        <Icon size={16} />
      </span>
      <div className={classes.text}>
        <Typography className={classes.title}>{heading}</Typography>
        {Boolean(subtitle) && <div className={classes.subtitle}>{subtitle}</div>}
      </div>
      {Boolean(action) && <div className={classes.actions}>{action}</div>}
    </div>
  );
});

interface WorkflowBannerActionProps {
  children: React.ReactNode;
  onClick: () => void;
  dataCy?: string;
  disabled?: boolean;
  loading?: boolean;
  /** Secondary actions are muted so the primary one reads first. */
  secondary?: boolean;
}

export const WorkflowBannerAction = React.memo(function WorkflowBannerAction({
  children,
  dataCy,
  disabled,
  loading,
  onClick,
  secondary,
}: WorkflowBannerActionProps): JSX.Element {
  const { classes } = useStyles({ severity: 'info' });
  return (
    <ButtonBase
      className={clsx(classes.action, secondary && classes.actionSecondary)}
      data-cy={dataCy}
      disabled={disabled || loading}
      onClick={onClick}
    >
      {loading && <CircularProgress color="inherit" size={12} />}
      {children}
    </ButtonBase>
  );
});

function getSeverityColor(theme: Theme, severity: WorkflowBannerSeverity): string {
  const colors: Record<WorkflowBannerSeverity, string> = {
    info: theme.palette.primary.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
  };
  return colors[severity];
}
